import React, { useState, useEffect } from 'react';
import { Download, Loader, Save, Calendar, Clock, MapPin, User, X, Check, Edit, AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

/**
 * AsignaturasEstudiante - Selección de paralelos con modal
 */
const AsignaturasEstudiante = () => {
  const PALETA_COLORES = [
    '#3B82F6', // Azul
    '#10B981', // Verde
    '#F59E0B', // Naranja
    '#8B5CF6', // Morado
    '#EC4899', // Rosado
    '#EF4444', // Rojo
    '#06B6D4', // Cian
    '#14B8A6', // Teal
    '#6366F1', // Indigo
  ];

  const obtenerColorPorCodigo = (codigo) => {
    if (!codigo) return PALETA_COLORES[0];
    let hash = 0;
    for (let i = 0; i < codigo.length; i++) {
      hash = codigo.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % PALETA_COLORES.length;
    return PALETA_COLORES[index];
  };

  const hexToRgba = (hex, alpha = 1) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };


  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gestion, setGestion] = useState(new Date().getFullYear());
  const [nivel, setNivel] = useState(1);

  const [asignaturas, setAsignaturas] = useState([]);
  const [selecciones, setSelecciones] = useState({});
  const [yaGuardo, setYaGuardo] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [seleccionHabilitada, setSeleccionHabilitada] = useState(true);
  const [periodoInfo, setPeriodoInfo] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Modal para selección de paralelo
  const [modalAsignatura, setModalAsignatura] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showModificar, setShowModificar] = useState(false);
  const [motivoMod, setMotivoMod] = useState('');
  const [sendingMod, setSendingMod] = useState(false);

  const dias = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

  const generarHoras = () => {
    const horas = [];
    for (let h = 6; h <= 18; h++) {
      horas.push(`${String(h).padStart(2, '0')}:00`);
      horas.push(`${String(h).padStart(2, '0')}:30`);
    }

    return horas;
  };
  const horas = generarHoras();

  // Función para obtener el rango del slot (ej: "08:00 - 08:30")
  const getSlotRango = (horaInicio) => {
    const [h, m] = horaInicio.split(':').map(Number);
    let finH = h;
    let finM = m + 30;
    if (finM >= 60) {
      finM = 0;
      finH += 1;
    }
    const horaFin = `${finH.toString().padStart(2, '0')}:${finM.toString().padStart(2, '0')}`;
    return `${horaInicio} - ${horaFin}`;
  };

  useEffect(() => {
    verificarPeriodoSeleccion();
    loadData();
  }, [gestion]);

  const verificarPeriodoSeleccion = async () => {
    try {
      // const response = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent('Selección de asignaturas (estudiantes)')}`);
      const response = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent('Inscripción de asignaturas (estudiante)')}`);
      const data = await response.json();
      setSeleccionHabilitada(data.habilitado);
      setPeriodoInfo(data);
    } catch (error) {
      console.error('Error verificando periodo:', error);
      setSeleccionHabilitada(true);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const url = `${API_URL}/estudiante/asignaturas-disponibles/${gestion}?id_usuario=${user.id_usuario || ''}`;

      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        console.log('[AsignaturasEstudiante] Backend response:', {
          yaGuardo: data.yaGuardo,
          horarioSeleccionado: data.horarioSeleccionado,
          nivel: data.nivel,
          asignaturasCount: data.asignaturas?.length
        });
        const sortAsig = (arr) => [...arr].sort((a, b) => {
          const nivelA = parseInt(a.nivel) || 0;
          const nivelB = parseInt(b.nivel) || 0;
          if (nivelA !== nivelB) return nivelA - nivelB;
          const numA = parseInt((a.codigo || '').replace(/\D/g, ''), 10) || 0;
          const numB = parseInt((b.codigo || '').replace(/\D/g, ''), 10) || 0;
          if (numA !== numB) return numA - numB;
          return (a.codigo || '').localeCompare(b.codigo || '');
        });
        setAsignaturas(sortAsig(data.asignaturas || []));
        setNivel(data.nivel || 1);
        setYaGuardo(data.yaGuardo || false);

        // Si ya guardó, reconstruir selecciones desde horarioSeleccionado
        // if (data.yaGuardo && data.horarioSeleccionado) {
        //   const prevSelecciones = {};
        //   const savedIds = data.horarioSeleccionado;

        //   data.asignaturas.forEach(asig => {
        //     // FIX: Comparación robusta de tipos (string vs number)
        //     const paraleloEncontrado = asig.paralelos.find(p =>
        //       savedIds.some(id => String(id) === String(p.id_asignacion))
        //     );
        //     if (paraleloEncontrado) {
        //       prevSelecciones[asig.id_asignatura] = paraleloEncontrado.id_asignacion;
        //     }
        //   });

        //   console.log('[AsignaturasEstudiante] Reconstrucced selections:', prevSelecciones);
        //   setSelecciones(prevSelecciones);
        // }
        if (data.yaGuardo && data.horarioSeleccionado) {
          const prevSelecciones = {};
          const savedIds = data.horarioSeleccionado.map(item => {
            if (item && typeof item === 'object') {
              return item.id || item.id_asignacion || item.asignatura;
            }
            return item;
          });

          console.log('[DEBUG] IDs normalizados:', savedIds);

          data.asignaturas.forEach(asig => {
            const paraleloEncontrado = asig.paralelos.find(p =>
              savedIds.some(id => String(id) === String(p.id_asignacion) || String(id) === String(p.id))
            );

            if (paraleloEncontrado) {
              prevSelecciones[asig.id_asignatura] = paraleloEncontrado.id_asignacion;
            }
          });

          setSelecciones(prevSelecciones);
        }
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeleccionParalelo = (idAsignatura, idAsignacion) => {
    if (yaGuardo) return;
    setSelecciones(prev => ({
      ...prev,
      [idAsignatura]: idAsignacion
    }));
    setModalAsignatura(null);
  };

  const getParaleloSeleccionado = (asignatura) => {
    const idAsignacion = selecciones[asignatura.id_asignatura];
    if (!idAsignacion) return null;
    return asignatura.paralelos.find(p => p.id_asignacion === idAsignacion);
  };

  const getHorariosEnCelda = (dia, hora) => {
    const resultado = [];
    asignaturas.forEach(asig => {
      const paralelo = getParaleloSeleccionado(asig);
      if (!paralelo) return;

      // Si tiene horarios_detalle, usar esos
      if (paralelo.horarios_detalle && Array.isArray(paralelo.horarios_detalle) && paralelo.horarios_detalle.length > 0) {
        paralelo.horarios_detalle.forEach(det => {
          if (det.dia === dia) {
            const horaInicio = det.hora_inicio?.substring(0, 5);
            const horaFin = det.hora_fin?.substring(0, 5);
            if (hora >= horaInicio && hora < horaFin) {
              resultado.push({ ...paralelo, codigo: asig.codigo, nombre: asig.nombre, sala: det.sala || paralelo.sala });
            }
          }
        });
      } else if (paralelo.dias?.includes(dia)) {
        const horaInicio = paralelo.hora_inicio?.substring(0, 5);
        const horaFin = paralelo.hora_fin?.substring(0, 5);
        if (hora >= horaInicio && hora < horaFin) {
          resultado.push({ ...paralelo, codigo: asig.codigo, nombre: asig.nombre });
        }
      }
    });
    return resultado;
  };

  const guardarHorario = async () => {
    if (!seleccionHabilitada) {
      setMensaje(`El periodo de selección de asignaturas no está habilitado.${periodoInfo?.fecha_inicio ? ` Fecha inicio: ${new Date(periodoInfo.fecha_inicio).toLocaleDateString()}` : ''}${periodoInfo?.fecha_fin ? ` - Fecha fin: ${new Date(periodoInfo.fecha_fin).toLocaleDateString()}` : ''}`);
      return;
    }
    const horariosIds = Object.values(selecciones).filter(Boolean);
    if (horariosIds.length === 0) {
      setMensaje('Debe seleccionar al menos un paralelo');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const response = await fetch(`${API_URL}/estudiante/horarios?id_usuario=${user.id_usuario || ''}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          gestion,
          horarios: horariosIds,
          es_modificacion: false,
          tipo: 'licenciatura'
        })
      });

      if (response.ok) {
        setYaGuardo(true);
        setMensaje('¡Horario guardado exitosamente!');
      } else {
        const error = await response.json();
        setMensaje('Error: ' + (error.message || 'No se pudo guardar'));
      }
    } catch (error) {
      setMensaje('Error al guardar el horario');
    } finally {
      setSaving(false);
    }
  };

  const enviarSolicitud = async () => {
    if (!motivoMod.trim()) {
      setMensaje('Debe especificar un motivo para la solicitud.');
      return;
    }
    setSendingMod(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const res = await fetch(`${API_URL}/estudiante/solicitud-modificacion`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ gestion, motivo: motivoMod })
      });

      const data = await res.json();
      if (res.ok) {
        setMensaje('Solicitud enviada. Dirección Académica revisará su caso.');
        setShowModificar(false);
      } else {
        setMensaje('Error: ' + data.message);
      }
    } catch (e) {
      setMensaje('Error al enviar la solicitud');
    } finally {
      setSendingMod(false);
    }
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // 1. Preparar la Grilla Semanal (Horas vs Días)
    const gridData = [];

    // Títulos y Notas
    gridData.push(['HORARIO DE CLASES - GESTIÓN ' + gestion]);
    gridData.push(['Generado el: ' + new Date().toLocaleString()]);
    gridData.push(['Nota: El siguiente horario se presenta únicamente con fines informativos y no tiene validez curricular.']);
    gridData.push([]); // Fila vacía

    // Encabezados de Días
    gridData.push(['Hora', ...dias]);

    // Llenar filas por cada slot de tiempo
    horas.forEach(hora => {
      const row = [getSlotRango(hora)];
      dias.forEach(dia => {
        const celdas = getHorariosEnCelda(dia, hora);
        if (celdas.length > 0) {
          // Formateamos el contenido de la celda: CODIGO - NOMBRE (PARALELO)
          row.push(celdas.map(h => `${h.codigo}: ${h.nombre} (P:${h.paralelo})`).join(' / '));
        } else {
          row.push('');
        }
      });
      gridData.push(row);
    });

    const wsGrid = XLSX.utils.aoa_to_sheet(gridData);

    // Ajustar anchos de columna básicos
    wsGrid['!cols'] = [
      { wch: 15 }, // Hora
      { wch: 30 }, // Lunes
      { wch: 30 }, // Martes
      { wch: 30 }, // Miércoles
      { wch: 30 }, // Jueves
      { wch: 30 }  // Viernes
    ];

    XLSX.utils.book_append_sheet(wb, wsGrid, "Horario Semanal");

    // 2. Hoja de Detalle de Materias
    const listData = [];
    asignaturas.forEach(asig => {
      const paralelo = getParaleloSeleccionado(asig);
      if (paralelo) {
        listData.push({
          'Código': asig.codigo,
          'Asignatura': asig.nombre,
          'Paralelo': paralelo.paralelo,
          'Docente': paralelo.docente,
          'Días y Horas': paralelo.horarios_detalle && Array.isArray(paralelo.horarios_detalle)
            ? paralelo.horarios_detalle.map(d => `${d.dia} ${d.hora_inicio?.substring(0, 5)}-${d.hora_fin?.substring(0, 5)} S:${d.sala || '-'}`).join(', ')
            : `${paralelo.dias?.join(', ') || '-'} ${paralelo.hora_inicio?.substring(0, 5)}-${paralelo.hora_fin?.substring(0, 5)}`,
          'Sala Principal': paralelo.sala || '-'
        });
      }
    });

    if (listData.length > 0) {
      const wsList = XLSX.utils.json_to_sheet(listData);
      XLSX.utils.book_append_sheet(wb, wsList, "Detalle de Materias");
    }

    // Descargar el archivo
    XLSX.writeFile(wb, `Horario_EBID_${gestion}.xlsx`);
    setShowDownloadModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin mr-2" /> Cargando asignaturas...
      </div>
    );
  }

  const cantidadSeleccionadas = Object.values(selecciones).filter(Boolean).length;

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Estudiante &gt; Inscripción Asignaturas</h1>
          {lastUpdated && (
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <Clock size={12} /> Actualizado: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <select
            value={gestion}
            onChange={(e) => setGestion(parseInt(e.target.value))}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            {(() => {
              const currentYear = new Date().getFullYear();
              return [currentYear - 1, currentYear, currentYear + 1].map(g => (
                <option key={g} value={g}>{g}</option>
              ));
            })()}
          </select>
          {yaGuardo && (
            <div className="flex gap-2">
              <button onClick={() => setShowDownloadModal(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                <Download size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {mensaje && (
        <div className={`mb-4 p-3 rounded-lg ${mensaje.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {mensaje}
        </div>
      )}

      {/* Banner de Periodo Cerrado */}
      {!seleccionHabilitada && !yaGuardo && (
        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="text-orange-500 mt-0.5" size={20} />
          <div>
            <p className="text-orange-800 font-medium">Periodo de inscripción cerrado</p>
            <p className="text-orange-600 text-sm">
              El periodo para la selección de asignaturas no está activo en este momento.
              {/* {periodoInfo?.fecha_inicio && periodoInfo?.fecha_fin ? ` (Del ${new Date(periodoInfo.fecha_inicio).toLocaleDateString()} al ${new Date(periodoInfo.fecha_fin).toLocaleDateString()})` : ''} */}
            </p>
          </div>
        </div>
      )}

      {yaGuardo && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 font-medium">✓ Horario guardado</p>
          <p className="text-blue-600 text-sm">Tu horario ya fue confirmado. Contacta a Dirección Académica para modificaciones.</p>
        </div>
      )}

      {/* Layout principal - 2 columnas si no guardó, 1 columna si ya guardó */}
      <div className={`grid gap-6 ${yaGuardo ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-[1fr_2fr]'}`}>
        {/* Lista de materias - SOLO SI NO HA GUARDADO */}
        {!yaGuardo && (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar size={20} className="text-blue-600" />
              Materias ({cantidadSeleccionadas}/{asignaturas.length} seleccionadas)
            </h2>

            <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
              {asignaturas.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No hay materias configuradas</p>
              ) : (
                [1, 2, 3, 4, 5].map(lv => {
                  const asigsNivel = asignaturas.filter(a => a.nivel === lv);
                  if (asigsNivel.length === 0) return null;

                  const nombresNiveles = {
                    1: 'Primer Año', 2: 'Segundo Año', 3: 'Tercer Año', 4: 'Cuarto Año', 5: 'Quinto Año'
                  };

                  return (
                    <div key={lv} className="space-y-3">
                      <div className="sticky top-0 bg-white/95 backdrop-blur-sm py-2 z-10 border-b border-blue-50">
                        <h3 className="text-xs font-black text-blue-900/40 uppercase tracking-[0.2em]">{nombresNiveles[lv]}</h3>
                      </div>
                      {asigsNivel.map(asig => {
                        const paraleloSeleccionado = getParaleloSeleccionado(asig);
                        return (
                          <div
                            key={asig.id_asignatura}
                            className={`p-4 rounded-xl border-2 transition-all hover:shadow-md ${paraleloSeleccionado ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-blue-300'
                              } ${yaGuardo || !seleccionHabilitada ? 'cursor-default opacity-75 grayscale' : 'cursor-pointer'}`}
                            onClick={() => !yaGuardo && seleccionHabilitada && setModalAsignatura(asig)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="font-mono text-[10px] text-gray-400 mb-0.5 tracking-tight">{asig.codigo}</p>
                                <p className="font-bold text-slate-700">{asig.nombre}</p>

                                {paraleloSeleccionado ? (
                                  <div className="mt-2 text-xs text-gray-600 space-y-1 bg-white/50 backdrop-blur-sm p-3 rounded-xl border border-green-100 shadow-sm">
                                    <div className="flex items-center gap-2">
                                      <span className="font-black text-green-700 text-[10px] bg-green-100 px-2 py-0.5 rounded uppercase tracking-wider">Paralelo {paraleloSeleccionado.paralelo}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-[10px] font-medium text-slate-500">
                                      <User size={12} className="text-slate-400" /> {paraleloSeleccionado.docente}
                                    </div>
                                    <div className="flex items-center gap-2 text-[10px] font-medium text-slate-500">
                                      <Clock size={12} className="text-slate-400" />
                                      {paraleloSeleccionado.horarios_detalle && Array.isArray(paraleloSeleccionado.horarios_detalle) && paraleloSeleccionado.horarios_detalle.length > 0
                                        ? paraleloSeleccionado.horarios_detalle.map((d, i) => (
                                          <span key={i}>{d.dia?.substring(0, 3)} {d.hora_inicio?.substring(0, 5)}-{d.hora_fin?.substring(0, 5)}{i < paraleloSeleccionado.horarios_detalle.length - 1 ? ' | ' : ''}</span>
                                        ))
                                        : <span>{paraleloSeleccionado.dias?.join(', ')} | {paraleloSeleccionado.hora_inicio?.substring(0, 5)}-{paraleloSeleccionado.hora_fin?.substring(0, 5)}</span>
                                      }
                                    </div>
                                  </div>
                                ) : (
                                  <p className="mt-1 text-[10px] font-bold text-blue-500/70 uppercase tracking-tight">
                                    {asig.paralelos.length} paralelo{asig.paralelos.length > 1 ? 's' : ''} disponible{asig.paralelos.length > 1 ? 's' : ''}
                                  </p>
                                )}
                              </div>
                              {paraleloSeleccionado && (
                                <div className="flex flex-col items-end justify-between ml-2 h-full gap-3">
                                  <Check className="text-green-600 mt-1" size={20} />
                                  {/* Asegúrate de que este botón sea un hijo directo del div con el onClick */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation(); // Evita que se dispare el onClick del div padre

                                      setSelecciones(prev => {
                                        const next = { ...prev };
                                        // USAR EL ID CORRECTO QUE SE USÓ PARA GUARDAR (id_asignatura)
                                        delete next[asig.id_asignatura];
                                        return next;
                                      });
                                    }}
                                    className="text-[10px] text-red-500 hover:text-red-700 font-semibold px-2 py-1 bg-red-50 rounded border border-red-100 hover:bg-red-100 transition-colors"
                                  >
                                    Quitar
                                  </button>
                                </div>
                              )}

                              {/* {paraleloSeleccionado && (
                                <div className="ml-2 bg-green-500 text-white p-1 rounded-full shadow-lg shadow-green-100">
                                  <Check size={18} strokeWidth={3} />
                                </div>
                              )} */}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>

            {!yaGuardo && seleccionHabilitada && asignaturas.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <button
                  onClick={guardarHorario}
                  disabled={saving || cantidadSeleccionadas === 0}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                >
                  {saving ? <Loader className="animate-spin" size={18} /> : <Save size={18} />}
                  Guardar Horario ({cantidadSeleccionadas}/{asignaturas.length})
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">⚠️ Solo podrás guardar una vez</p>
              </div>
            )}
          </div>
        )}

        {/* Vista previa horario */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">
              {yaGuardo ? 'Mi Horario de Clases' : 'Vista Previa del Horario'}
            </h2>
            {!yaGuardo && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider animate-pulse">
                Modo Selección
              </span>
            )}
          </div>

          {cantidadSeleccionadas === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-gray-100 rounded-2xl bg-gray-50/30">
              <div className="text-4xl mb-4">🗓️</div>
              <p className="text-gray-400 text-sm font-medium">El horario se pintará aquí una vez que</p>
              <p className="text-gray-500 font-bold">inscribas tus materias</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 min-w-[110px]">Hora</th>
                    {dias.map(dia => <th key={dia} className="border p-2 text-center min-w-[160px] md:min-w-[180px]">{dia}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {horas.map((hora, hIdx) => (
                    <tr key={hora}>
                      <td className="border border-gray-200 p-2 text-center text-gray-500 font-mono bg-gray-50 whitespace-nowrap text-[11px]">
                        {getSlotRango(hora)}
                      </td>
                      {dias.map(dia => {
                        const horarios = getHorariosEnCelda(dia, hora);
                        const hayContenido = horarios.length > 0;

                        // Para el "pintado completo", verificamos si la materia continúa de la celda de arriba
                        // Esto solo funciona si hay una sola materia en la celda (lo normal para un horario personal)
                        let esContinuacion = false;
                        if (hIdx > 0 && hayContenido && horarios.length === 1) {
                          const horaAnterior = horas[hIdx - 1];
                          const horariosArriba = getHorariosEnCelda(dia, horaAnterior);
                          if (horariosArriba.length === 1 && horariosArriba[0].id_asignacion === horarios[0].id_asignacion) {
                            esContinuacion = true;
                          }
                        }

                        return (
                          <td
                            key={`${dia}-${hora}`}
                            className={`border-x border-gray-200 p-0 h-16 align-top min-w-[120px] ${esContinuacion ? 'border-t-0' : 'border-t border-gray-200'} ${hIdx === horas.length - 1 ? 'border-b border-gray-200' : ''}`}
                          >
                            <div className={`flex flex-col h-full w-full ${esContinuacion ? '-mt-[1px]' : ''}`}>
                              {horarios.map((h, idx) => {
                                const colorSolido = obtenerColorPorCodigo(h.codigo);

                                return (
                                  <div
                                    key={idx}
                                    className={`flex-1 text-[10px] p-2 leading-tight shadow-inner rounded-none flex flex-col justify-center transition-all ${esContinuacion ? 'pt-0' : ''}`}
                                    style={{
                                      // Si ya guardó, sólido. Si es vista previa, claro.
                                      backgroundColor: yaGuardo ? colorSolido : hexToRgba(colorSolido, 0.2),
                                      // Borde lateral para identificar la materia en vista previa
                                      borderLeft: !yaGuardo ? `4px solid ${colorSolido}` : 'none',
                                      // Texto blanco si está guardado, oscuro si es vista previa
                                      color: yaGuardo ? 'white' : '#1e293b'
                                    }}
                                    title={`${h.codigo} - ${h.nombre}\n${h.docente}\nSala ${h.sala || '-'}`}
                                  >
                                    {!esContinuacion && (
                                      <>
                                        <div className="font-bold truncate">{h.codigo}</div>
                                        <div className="opacity-90 truncate max-w-full text-[9px] uppercase tracking-tighter">{h.nombre}</div>
                                        <div className="opacity-80 text-[8px] mt-0.5">Par: {h.paralelo} | S: {h.sala || '-'}</div>
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para seleccionar paralelo */}
      {modalAsignatura && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b">
                {/* <p className="font-mono text-sm opacity-80">{modalAsignatura.codigo}</p> */}
                <h3 className="text-xl font-bold text-blue-900">{modalAsignatura.nombre}</h3>
              <button onClick={() => setModalAsignatura(null)} >
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              <p className="text-gray-600 mb-4 text-sm">Selecciona un paralelo:</p>

              <div className="space-y-3 max-h-80 overflow-y-auto">
                {modalAsignatura.paralelos.map(p => {
                  const isSelected = selecciones[modalAsignatura.id_asignatura] === p.id_asignacion;

                  return (
                    <div
                      key={p.id_asignacion}
                      className={`p-4 rounded-lg border-2 transition-all hover:shadow-md ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-blue-400'
                        } ${(!isSelected && p.inscritos >= p.capacidad) ? 'opacity-60 bg-gray-50 cursor-not-allowed border-dashed' : 'cursor-pointer'}`}
                      onClick={() => !yaGuardo && (isSelected || p.inscritos < p.capacidad) && handleSeleccionParalelo(modalAsignatura.id_asignatura, p.id_asignacion)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span
                              className="px-3 py-1 rounded-full text-white text-sm font-bold"
                              style={{ backgroundColor: p.color || '#3B82F6' }}
                            >
                              Paralelo {p.paralelo}
                            </span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${p.inscritos >= p.capacidad ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                              Cupos: {p.inscritos || 0}/{p.capacidad || 0}
                              {p.inscritos >= p.capacidad && ' - LLENO'}
                            </span>
                          </div>

                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2 text-gray-700">
                              <User size={14} className="text-gray-400" />
                              <span className="font-medium">{p.docente}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Calendar size={14} className="text-gray-400" />
                              <span>{p.dias?.join(', ') || 'Sin días'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Clock size={14} className="text-gray-400" />
                              {p.horarios_detalle && Array.isArray(p.horarios_detalle) && p.horarios_detalle.length > 0
                                ? <div className="space-y-0.5">
                                  {p.horarios_detalle.map((d, i) => (
                                    <div key={i}><span className="font-medium">{d.dia?.substring(0, 3)}</span> {d.hora_inicio?.substring(0, 5)}-{d.hora_fin?.substring(0, 5)} S{d.sala || '-'}</div>
                                  ))}
                                </div>
                                : <span>{p.hora_inicio?.substring(0, 5)} - {p.hora_fin?.substring(0, 5)}</span>
                              }
                            </div>
                          </div>
                        </div>

                        {isSelected && (
                          <div className="ml-4">
                            <Check size={28} className="text-green-600" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => setModalAsignatura(null)}
                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de descarga */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-center">
            <AlertTriangle className="mx-auto mb-4 text-yellow-500" size={48} />
            <h3 className="text-lg font-bold mb-2">Aviso Importante</h3>
            <p className="text-sm text-gray-600 mb-6">
              Nota: El siguiente horario se presenta únicamente con fines informativos y no tiene validez curricular.
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setShowDownloadModal(false)} className="px-4 py-2 border rounded hover:bg-gray-100">Cancelar</button>
              <button onClick={exportToExcel} className="px-4 py-2 bg-blue-900 text-white rounded hover:bg-blue-800">Aceptar y Descargar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modificar Horario */}
      {showModificar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Solicitud de Modificación</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                Esta solicitud se enviará a Dirección Académica para su aprobación. Solo puedes realizar esto <strong>una sola vez</strong>.
              </p>
            </div>
            <textarea
              className="w-full border rounded p-2 mb-4 text-sm"
              rows="3"
              placeholder="Explique el motivo del cambio..."
              value={motivoMod}
              onChange={(e) => setMotivoMod(e.target.value)}
            ></textarea>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowModificar(false)} disabled={sendingMod} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
              <button onClick={enviarSolicitud} disabled={sendingMod} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2">
                {sendingMod && <Loader className="animate-spin" size={14} />} Enviar Solicitud
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AsignaturasEstudiante;